import { isAxiosError } from 'axios';

/**
 * Mirrors the error envelope from 07-cross-cutting.md §5 / openapi.yaml `ErrorEnvelope`:
 *
 * {
 *   "error": {
 *     "code": "VALIDATION_ERROR",
 *     "message": "Validation failed",
 *     "details": [{ "field": "nameAr", "message": "must not be empty" }],
 *     "warnings": [{ "field": "nationalId", "message": "checksum does not match" }],
 *     "traceId": "...",
 *     "timestamp": "...",
 *     "path": "/api/v1/students"
 *   }
 * }
 */
export interface ApiErrorDetail {
  field?: string;
  message: string;
}

export interface ApiErrorEnvelope {
  error: {
    code: string;
    message: string;
    details?: ApiErrorDetail[];
    warnings?: ApiErrorDetail[];
    traceId: string;
    timestamp: string;
    path?: string;
  };
}

/** Canonical error codes from 07-cross-cutting.md §5. */
export type ApiErrorCode =
  | 'AUTH_INVALID_CREDENTIALS'
  | 'AUTH_ACCOUNT_DISABLED'
  | 'AUTH_TOO_MANY_ATTEMPTS'
  | 'TOKEN_EXPIRED'
  | 'TOKEN_INVALID'
  | 'FORBIDDEN'
  | 'NOT_FOUND'
  | 'VALIDATION_ERROR'
  | 'DUPLICATE_EMAIL'
  | 'STUDENT_CODE_IMMUTABLE'
  | 'INSTALLMENTS_SUM_MISMATCH'
  | 'PAID_INSTALLMENT_LOCKED'
  | 'ACTIVE_PLAN_NOT_COMPLETED'
  | 'PLAN_ARCHIVED_READONLY'
  | 'INVALID_STATUS_TRANSITION'
  | 'LOOKUP_VALUE_INACTIVE'
  | 'CAPTCHA_FAILED'
  | 'RATE_LIMITED'
  | 'INTERNAL_ERROR'
  | (string & {});

/** Normalized, typed error thrown by the API client for every failed request. */
export class ApiError extends Error {
  readonly code: ApiErrorCode;
  readonly status: number | null;
  readonly details: ApiErrorDetail[];
  readonly warnings: ApiErrorDetail[];
  readonly traceId: string | null;

  constructor(params: {
    code: ApiErrorCode;
    message: string;
    status: number | null;
    details?: ApiErrorDetail[];
    warnings?: ApiErrorDetail[];
    traceId?: string | null;
  }) {
    super(params.message);
    this.name = 'ApiError';
    this.code = params.code;
    this.status = params.status;
    this.details = params.details ?? [];
    this.warnings = params.warnings ?? [];
    this.traceId = params.traceId ?? null;
  }

  static fromUnknown(err: unknown): ApiError {
    if (err instanceof ApiError) return err;

    if (isAxiosError<ApiErrorEnvelope>(err)) {
      const status = err.response?.status ?? null;
      const envelope = err.response?.data;

      if (envelope?.error) {
        return new ApiError({
          code: envelope.error.code as ApiErrorCode,
          message: envelope.error.message,
          status,
          details: envelope.error.details,
          warnings: envelope.error.warnings,
          traceId: envelope.error.traceId,
        });
      }

      if (err.code === 'ERR_NETWORK' || !err.response) {
        return new ApiError({
          code: 'NETWORK_ERROR',
          message: 'Could not reach the server. Please check your connection and try again.',
          status: null,
        });
      }

      return new ApiError({
        code: 'INTERNAL_ERROR',
        message: err.message || 'Unexpected server error.',
        status,
      });
    }

    return new ApiError({
      code: 'INTERNAL_ERROR',
      message: err instanceof Error ? err.message : 'Unexpected error.',
      status: null,
    });
  }
}
